/* AI image badge for Shopify themes.
 *
 * Marks AI images on the storefront with the European Commission's AI
 * transparency icons, as an overlay. The image file is never modified.
 *
 * How an AI image is recognised
 * -----------------------------
 * By a marker in its file name. Shopify keeps the original file name in
 * the CDN URL, which makes it the only AI signal that reaches a theme:
 * Shopify's CDN strips embedded image metadata (EXIF / IPTC / C2PA), and
 * per-image metafields are deprecated in the Admin API.
 *
 * Why the badge is not burnt into the image
 * ----------------------------------------
 * Because the merchant asked for the file to stay untouched. Note the
 * trade-off: an overlay is visible on the storefront but not when the
 * image is right-click-saved, hotlinked into Google Shopping or pulled
 * into a Meta catalogue. The EU guidance asks for a mark that survives
 * resharing and downloading, so treat this as the storefront half of
 * that job.
 */
(function () {
  'use strict';

  /* The badge ships two ways: a section you add per template, and a
   * snippet rendered site-wide from theme.liquid. A theme with both
   * installed runs this script twice on one page - two observers, two
   * resize listeners, two scans of every image. First one wins. */
  if (window.__aiImageBadgeLoaded) return;
  window.__aiImageBadgeLoaded = true;

  var configEl = document.getElementById('ai-image-badge-config');
  if (!configEl) return;

  var cfg;
  try {
    cfg = JSON.parse(configEl.textContent);
  } catch (e) {
    console.warn('[ai-image-badge] could not read its configuration:', e);
    return;
  }

  var markers = {
    generated: (cfg.markers && cfg.markers.generated || '').trim().toLowerCase(),
    modified: (cfg.markers && cfg.markers.modified || '').trim().toLowerCase(),
  };
  if (!markers.generated && !markers.modified) return;   // nothing to look for

  var corner = cfg.corner || 'bottom-right';
  var sizePercent = Number(cfg.sizePercent) || 12;
  /* On a phone the product image fills the screen width, so a percentage
   * that reads as a small corner mark on desktop becomes a banner across
   * the photo. Phones get their own percentage and a hard pixel ceiling
   * on the visible mark. 0 disables the ceiling. */
  var sizePercentMobile = Number(cfg.sizePercentMobile) || sizePercent;
  var maxPxMobile = Number(cfg.maxPxMobile);
  if (isNaN(maxPxMobile)) maxPxMobile = 110;
  var mobileBreakpoint = Number(cfg.mobileBreakpoint) || 750;
  var marginPercent = Number(cfg.marginPercent);
  if (isNaN(marginPercent)) marginPercent = 4;
  var minImagePx = Number(cfg.minImagePx) || 0;
  var excludeSelector = (cfg.exclude || '').trim();
  var showInZoom = cfg.showInZoom === true;
  var BADGE_ATTR = 'data-ai-badged';

  /* The official artwork does not fill its own viewBox: each mark sits in
   * a box with transparent margins, and they are not the same on every
   * side. Measured from the shipped SVGs with getBBox(), as a fraction of
   * the box:
   *
   *   AI GENERATED   top 25.5%  right 11.1%  bottom 27.5%  left 11.6%
   *   AI MODIFIED    top 25.5%  right 14.1%  bottom 27.5%  left 13.6%
   *   AI (circle)    top 17.8%  right 19.8%  bottom 17.8%  left 15.7%
   *
   * Placing the box at the requested distance therefore places the mark
   * somewhere else, differently on each axis and for each icon, so "4%"
   * meant nothing you could predict. Compensating here makes the size and
   * distance settings describe the mark the shopper actually sees. A
   * merchant's own badge is assumed to be cropped tight. */
  var ICON_GEOMETRY = {
    generated: { aspect: 3.1571, t: 0.2546, r: 0.1108, b: 0.2754, l: 0.1158 },
    modified:  { aspect: 3.0000, t: 0.2546, r: 0.1406, b: 0.2754, l: 0.1359 },
    basic:     { aspect: 1.0000, t: 0.1777, r: 0.1978, b: 0.1777, l: 0.1575 },
  };
  var NO_GEOMETRY = { aspect: 0, t: 0, r: 0, b: 0, l: 0 };

  var ICON_RESET = [
    ['display', 'block'], ['width', '100%'], ['height', 'auto'],
    ['min-width', '0'], ['min-height', '0'],
    ['max-width', 'none'], ['max-height', 'none'],
    ['object-fit', 'fill'], ['position', 'static'],
    ['margin', '0'], ['padding', '0'], ['border', '0'],
    ['transform', 'none'], ['aspect-ratio', 'auto'],
  ];

  function geometryFor(src) {
    if (cfg.useCustom && cfg.icons && cfg.icons.custom) return NO_GEOMETRY;
    var name = String(src || '').split('/').pop();
    if (name.indexOf('eu-ai-generated') === 0) return ICON_GEOMETRY.generated;
    if (name.indexOf('eu-ai-modified') === 0) return ICON_GEOMETRY.modified;
    if (name.indexOf('eu-ai-basic') === 0) return ICON_GEOMETRY.basic;
    return NO_GEOMETRY;
  }

  /* Lightbox, zoom and quick-view containers across the common themes and
   * gallery libraries. A zoomed image is a deliberate close look at the
   * product, where a mark sized for a gallery thumbnail becomes absurd and
   * covers the thing the shopper opened it to see. Skipped by default; the
   * PDP, cards and collection grid still carry the mark. */
  var ZOOM_SELECTORS = [
    '.product-media-modal', '.product__modal-opener--image[open]', 'product-modal',
    '.pswp', '.fancybox__container', '.lightbox', '.lg-container', '.glightbox-container',
    '.zoomImg', '.drift-zoom-pane', '.medium-zoom-image--opened', '.image-zoom',
    '[data-lightbox]', '[data-fancybox]', 'dialog[open]', '[role="dialog"]',
    '.modal.is-open', '.modal--open', '.quick-view',
  ].join(', ');

  /* ---------------------------------------------------------------- */
  /* Detection                                                        */
  /* ---------------------------------------------------------------- */

  /* Every URL an <img> might be showing or about to show. A lazy-loading
   * theme keeps the real URL in data-src until the image scrolls into
   * view, and srcset carries the variants Shopify generated — the marker
   * is in the file name, so it is present in all of them. */
  function urlsFor(img) {
    var out = [];
    ['currentSrc', 'src'].forEach(function (k) {
      if (img[k]) out.push(img[k]);
    });
    ['data-src', 'data-srcset', 'srcset', 'data-original'].forEach(function (a) {
      var v = img.getAttribute(a);
      if (v) out.push(v);
    });
    return out;
  }

  /* Reduce a URL to just its file name.
   *
   * Matching the whole URL would let a shop domain, theme-asset path or
   * collection handle that happens to contain the marker badge every
   * image on the page. Only the last path segment is the uploaded file
   * name, which is the thing that actually carries the marker.
   *
   * A srcset entry is "url 400w, url 800w" — each candidate is split out
   * first. Percent-encoding is undone because Shopify encodes spaces and
   * non-ASCII in the path. */
  function fileNamesIn(value) {
    var names = [];
    String(value).split(',').forEach(function (part) {
      var url = part.trim().split(/\s+/)[0];
      if (!url) return;
      var path;
      try {
        path = new URL(url, window.location.href).pathname;
      } catch (e) {
        path = url.split('?')[0];
      }
      var name = path.split('/').pop() || '';
      try {
        name = decodeURIComponent(name);
      } catch (e) { /* malformed escape — match the raw form */ }
      if (name) names.push(name.toLowerCase());
    });
    return names;
  }

  /* Which mark an image should carry, or null.
   *
   * "generated" wins over "modified": an image that somehow carries both
   * markers is the stronger claim. Matching is a lower-cased substring
   * test, because Shopify sanitises file names (case, spaces) but leaves
   * an alphanumeric marker intact. */
  function markFor(img) {
    var names = [];
    urlsFor(img).forEach(function (u) {
      names = names.concat(fileNamesIn(u));
    });
    if (!names.length) return null;
    var haystack = names.join(' ');
    if (markers.generated && haystack.indexOf(markers.generated) !== -1) return 'generated';
    if (markers.modified && haystack.indexOf(markers.modified) !== -1) return 'modified';
    return null;
  }

  function isExcluded(img) {
    if (!showInZoom) {
      try {
        if (img.closest(ZOOM_SELECTORS)) return true;
      } catch (e) { /* a selector this browser dislikes must not break the page */ }
      /* Some zoom plugins clone the image into a full-viewport layer
       * rather than a recognisable container. Anything rendered at
       * roughly the viewport's own size is that, not a gallery image. */
      var r = img.getBoundingClientRect();
      if (r.width >= window.innerWidth * 0.92 && r.height >= window.innerHeight * 0.8) {
        return true;
      }
    }
    if (!excludeSelector) return false;
    try {
      return !!img.closest(excludeSelector);
    } catch (e) {
      return false;                       // a bad selector must not break the page
    }
  }

  /* Skip images too small to carry a legible mark — thumbnails, swatches,
   * cart line items. Zero disables the check. An image that has not laid
   * out yet reports 0 and is retried on the next pass. */
  function tooSmall(img) {
    if (!minImagePx) return false;
    var w = img.naturalWidth || img.width || img.getBoundingClientRect().width;
    return w > 0 && w < minImagePx;
  }

  /* ---------------------------------------------------------------- */
  /* Rendering                                                        */
  /* ---------------------------------------------------------------- */

  function iconFor(kind) {
    if (cfg.useCustom && cfg.icons && cfg.icons.custom) return cfg.icons.custom;
    if (!cfg.icons) return null;
    /* The round "AI" logo says the same for generated and modified images;
     * the screen-reader label still tells them apart. */
    if (cfg.iconType === 'basic' && cfg.icons.basic) return cfg.icons.basic;
    return cfg.icons[kind] || cfg.icons.basic || null;
  }

  /* The badge lives inside the image's nearest positioned ancestor. When
   * that ancestor computes to static — common in Dawn-style markup — it is
   * promoted to relative and flagged, so the change is one attribute and
   * visible in devtools rather than an invisible inline style. */
  function hostFor(img) {
    var el = img.parentElement;
    var depth = 0;
    while (el && depth < 4) {
      var pos = window.getComputedStyle(el).position;
      if (pos !== 'static') return el;
      if (el.hasAttribute(BADGE_ATTR + '-host')) return el;
      el = el.parentElement;
      depth++;
    }
    var parent = img.parentElement;
    if (parent) parent.setAttribute('data-ai-badge-host', '');
    return parent;
  }

  /* The box the image is actually PAINTED in, which is not its element
   * box whenever object-fit letterboxes it. A percentage width on the
   * badge resolves against its positioned host, so on a page where the
   * host is wider than the painted image — a lightbox, or any container
   * with object-fit: contain — the mark comes out far too large and
   * floats off the photo. Measuring the painted box and sizing in pixels
   * keeps the mark proportional to the image itself. */
  function paintedRect(img) {
    var r = img.getBoundingClientRect();
    var nw = img.naturalWidth, nh = img.naturalHeight;
    if (!nw || !nh || !r.width || !r.height) return r;
    var fit = window.getComputedStyle(img).objectFit || 'fill';
    if (fit === 'fill' || fit === 'none') return r;
    var scale = (fit === 'cover')
      ? Math.max(r.width / nw, r.height / nh)
      : Math.min(r.width / nw, r.height / nh);
    var w = nw * scale, h = nh * scale;
    if (w >= r.width && h >= r.height) return r;       // fills the box anyway
    var left = r.left + (r.width - w) / 2;
    var top = r.top + (r.height - h) / 2;
    /* right and bottom are computed, not inherited: a plain object from a
     * DOMRect has neither, and reading an absent one yields NaN offsets
     * that the browser then discards without complaint. */
    return { left: left, top: top, right: left + w, bottom: top + h, width: w, height: h };
  }

  /* Size and position the badge against the painted image, expressed in
   * pixels relative to the host. Recomputed whenever the layout can have
   * changed, so the mark tracks a responsive image. */
  function layout(badge, img, host) {
    var pr = paintedRect(img);
    if (!pr.width) return;
    var hr = host.getBoundingClientRect();
    var iconEl = badge.querySelector('img');
    var geom = geometryFor(iconEl && iconEl.getAttribute('src'));

    /* sizePercent describes the visible mark, so the element has to be
     * larger by whatever transparent margin the artwork carries. */
    var mobile = window.innerWidth < mobileBreakpoint;
    var visibleW = pr.width * ((mobile ? sizePercentMobile : sizePercent) / 100);
    if (mobile && maxPxMobile > 0) visibleW = Math.min(visibleW, maxPxMobile);
    var boxW = Math.max(16, visibleW / (1 - geom.l - geom.r));
    var boxH = geom.aspect ? boxW / geom.aspect
                           : badge.getBoundingClientRect().height;
    var inset = pr.width * (marginPercent / 100);

    var topOffset = (pr.top - hr.top) + inset - boxH * geom.t;
    var leftOffset = (pr.left - hr.left) + inset - boxW * geom.l;
    var rightOffset = (hr.right - pr.right) + inset - boxW * geom.r;
    var bottomOffset = (hr.bottom - pr.bottom) + inset - boxH * geom.b;

    if (![boxW, topOffset, leftOffset, rightOffset, bottomOffset].every(isFinite)) return;

    /* Called on every animation frame while a slider moves; rewriting
     * identical values would still churn the DOM. */
    var key = [boxW, topOffset, leftOffset, rightOffset, bottomOffset]
      .map(function (n) { return Math.round(n * 10); }).join(',');
    if (badge.getAttribute('data-ai-badge-layout') === key) return;
    badge.setAttribute('data-ai-badge-layout', key);

    /* !important on every geometry property, because a theme's own badge
     * styling can otherwise beat an inline style and drag the mark into
     * the corner. Themes ship rules like
     * `.product__media [class*="badge"] { bottom: 0 !important }`, which
     * silently wins on one axis and leaves the other looking correct. */
    set(badge, 'width', boxW + 'px');
    set(badge, 'visibility', 'visible');
    ['top', 'bottom', 'left', 'right'].forEach(function (k) { set(badge, k, ''); });
    set(badge, corner.indexOf('top') === 0 ? 'top' : 'bottom',
        (corner.indexOf('top') === 0 ? topOffset : bottomOffset) + 'px');
    set(badge, corner.indexOf('right') !== -1 ? 'right' : 'left',
        (corner.indexOf('right') !== -1 ? rightOffset : leftOffset) + 'px');
  }

  function set(el, prop, value) {
    if (value === '') el.style.removeProperty(prop);
    else el.style.setProperty(prop, value, 'important');
  }

  var placed = [];          /* [badge, img, host] — relaid out on resize */

  function place(img, kind) {
    var host = hostFor(img);
    if (!host) return;

    var src = iconFor(kind);
    if (!src) return;

    var badge = document.createElement('span');
    badge.className = 'eu-ai-mark eu-ai-mark--' + corner;
    badge.setAttribute('data-ai-badge', kind);
    set(badge, 'position', 'absolute');
    set(badge, 'margin', '0');
    /* Hidden until layout() has measured the image and sized the mark.
     * Unsized, the SVG falls back to the browser's default 300px and
     * covers half a phone-width photo. */
    set(badge, 'visibility', 'hidden');

    var label = (cfg.labels && cfg.labels[kind]) ||
                (kind === 'generated' ? 'Image generated with AI' : 'Image modified with AI');

    var icon = document.createElement('img');
    icon.src = src;
    icon.alt = '';                      /* decorative: the text below carries it */
    icon.setAttribute('aria-hidden', 'true');
    icon.loading = 'lazy';
    icon.decoding = 'async';
    /* The mark is an <img> inside the theme's own markup, so every rule the
     * theme writes for its product images applies to it too. A single
     * `.product__media img { height: 100% }` - and themes do write exactly
     * that - stretches the mark to the full height of the photo. Our
     * stylesheet cannot reliably outrank it: `.eu-ai-mark img` and
     * `.product__media img` have identical specificity, so whichever
     * stylesheet loads last wins, and load order is the theme's to decide.
     * Pin it inline instead. */
    ICON_RESET.forEach(function (pair) { set(icon, pair[0], pair[1]); });
    badge.appendChild(icon);

    var sr = document.createElement('span');
    sr.className = 'eu-ai-mark__sr';
    sr.textContent = label;
    badge.appendChild(sr);

    /* Also expose it on the image itself, so a screen reader that reads
     * the image alone still gets the disclosure. */
    var existingAlt = img.getAttribute('alt') || '';
    if (existingAlt.toLowerCase().indexOf('ai') === -1) {
      img.setAttribute('alt', existingAlt ? existingAlt + ' — ' + label : label);
    }

    host.appendChild(badge);
    img.setAttribute(BADGE_ATTR, kind);
    placed.push([badge, img, host]);
    layout(badge, img, host);
    /* A lazily-decoded image reports naturalWidth only once it has loaded,
     * so the first measurement can be wrong. Redo it then. */
    if (!img.complete) {
      img.addEventListener('load', function () { layout(badge, img, host); }, { once: true });
    }
  }

  function relayout() {
    var handedOver = false;
    for (var i = placed.length - 1; i >= 0; i--) {
      var entry = placed[i];
      if (!entry[1].isConnected || !entry[0].isConnected) { placed.splice(i, 1); continue; }
      /* The badged copy of a stacked pair can be the one the theme later
       * hides - a carousel slide scrolled out, a closed quick-view. Drop
       * the mark so the visible copy can claim it on the next pass,
       * otherwise the disclosure disappears with the hidden image. */
      if (isHidden(entry[1])) {
        clear(entry[1]);
        if (waiting.indexOf(entry[1]) === -1) waiting.push(entry[1]);
        handedOver = true;
        continue;
      }
      layout(entry[0], entry[1], entry[2]);
    }
    if (handedOver) scheduleScan();
  }

  function clear(img) {
    var kind = img.getAttribute(BADGE_ATTR);
    if (!kind) return;
    img.removeAttribute(BADGE_ATTR);
    /* Remove this image's own mark. Several images can share one host - a
     * theme's hidden zoom source sits next to the visible photo - so the
     * first mark found in the host is not necessarily this one. */
    for (var i = placed.length - 1; i >= 0; i--) {
      if (placed[i][1] === img) {
        placed[i][0].remove();
        placed.splice(i, 1);
      }
    }
  }

  /* ---------------------------------------------------------------- */
  /* Scanning                                                         */
  /* ---------------------------------------------------------------- */

  /* Not on screen: no box at all (display:none - a theme's hidden zoom
   * source, a slide not laid out), or laid out but invisible (a fade
   * slider stacks every slide in one place and fades out all but the
   * active one). Such an image has nothing to size a mark against, and a
   * mark on it is a mark the shopper never sees. */
  function isHidden(img) {
    var r = img.getBoundingClientRect();
    if (!r.width || !r.height) return true;
    for (var el = img, d = 0; el && el !== document.body && d < 10; el = el.parentElement, d++) {
      var cs = window.getComputedStyle(el);
      if (cs.visibility === 'hidden' || Number(cs.opacity) < 0.05) return true;
    }
    return false;
  }

  /* Galleries stack copies of the same photo: a zoom layer over the main
   * image, a scroll-snap slide behind the active one, a quick-view clone.
   * Each is a separate <img>, each carries the marker, and each was
   * getting its own mark - two or three of them a few pixels apart over
   * one picture. Badge only the first of an overlapping set. Judged on
   * the painted boxes rather than on the DOM, because the markup that
   * produces a stacked duplicate looks different in every theme. */
  function isShadowed(img) {
    var a = paintedRect(img);
    if (!a.width || !a.height) return false;
    for (var i = 0; i < placed.length; i++) {
      var other = placed[i][1];
      if (other === img || !other.isConnected || isHidden(other)) continue;
      var b = paintedRect(other);
      if (!b.width || !b.height) continue;
      var w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
      var h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
      if (w <= 0 || h <= 0) continue;
      var smaller = Math.min(a.width * a.height, b.width * b.height);
      if (smaller > 0 && (w * h) / smaller > 0.6) return true;
    }
    return false;
  }

  function consider(img) {
    var want = markFor(img);
    var have = img.getAttribute(BADGE_ATTR);

    /* A variant change swaps the src in place. If the new image is not an
     * AI image the old badge has to go, and vice versa. */
    if (have && have !== want) clear(img);
    if (!want) return;
    if (img.getAttribute(BADGE_ATTR)) return;
    if (isExcluded(img) || tooSmall(img)) return;
    /* Parked and re-checked when a class or style changes or a
     * transition ends, which is how sliders and galleries reveal a slide.
     * Placing a mark now would size it against nothing, and the SVG would
     * fall back to the browser's default 300px. */
    if (isHidden(img)) {
      if (waiting.indexOf(img) === -1) waiting.push(img);
      return;
    }
    if (isShadowed(img)) return;
    place(img, want);
  }

  var waiting = [];         /* AI images skipped because not on screen */

  function recheckWaiting() {
    for (var i = waiting.length - 1; i >= 0; i--) {
      var img = waiting[i];
      if (!img.isConnected) { waiting.splice(i, 1); continue; }
      if (isHidden(img)) continue;
      waiting.splice(i, 1);
      consider(img);
    }
    /* A slide that just faded out still holds its mark; hand it over. */
    relayout();
  }

  function scan(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var images = scope.querySelectorAll('img');
    for (var i = 0; i < images.length; i++) consider(images[i]);
    if (root && root.tagName === 'IMG') consider(root);
  }

  var relayoutPending = false;
  var pending = false;
  function scheduleScan() {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(function () {
      pending = false;
      scan(document);
      /* Opening or closing a lightbox moves images around without
       * changing their src; anything already badged may need re-placing. */
      relayout();
    });
  }

  /* Themes swap product images on variant change, load more on scroll and
   * render quick-view modals after the fact. A single pass at load would
   * miss all of it, so watch for added nodes and for src/srcset changing
   * on an image already in the DOM. */
  var recheckPending = false;
  function scheduleRecheck(force) {
    if (recheckPending || (!waiting.length && !force)) return;
    recheckPending = true;
    window.requestAnimationFrame(function () {
      recheckPending = false;
      recheckWaiting();
    });
  }

  /* class and style changes are frequent - every slider animation frame -
   * so they only re-check the few images parked as not rendered, never
   * the whole page. */
  var observer = new MutationObserver(function (records) {
    var reveal = false;
    for (var i = 0; i < records.length; i++) {
      var r = records[i];
      if (r.type === 'attributes') {
        /* layout() restyles the marks; reacting to that would loop. */
        if (r.target.closest && r.target.closest('.eu-ai-mark')) continue;
        if (r.attributeName === 'class' || r.attributeName === 'style') { reveal = true; continue; }
        scheduleScan(); return;
      }
      if (r.addedNodes && r.addedNodes.length) { scheduleScan(); return; }
    }
    if (reveal) scheduleRecheck();
  });

  function start() {
    /* Marks from an older copy of this file still living in the theme
     * under a second asset name. Both scripts run, both draw, and the
     * page ends up with two marks a few pixels apart in slightly
     * different sizes. Sweeping them is only half a fix - the other copy
     * has to be deleted from the theme - but it stops the page looking
     * broken in the meantime. */
    var stale = document.querySelectorAll('.ai-badge');
    for (var s = 0; s < stale.length; s++) stale[s].remove();

    scan(document);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset', 'data-src', 'data-srcset', 'class', 'style'],
    });
    /* A fade or slide animation ends without touching the DOM. */
    document.addEventListener('transitionend', function () {
      scheduleRecheck(true);
    }, true);
    /* Lazy-loaded images report their real size only once decoded. */
    window.addEventListener('load', scheduleScan, { once: true });
    /* A responsive image changes size without changing src. */
    window.addEventListener('resize', function () {
      if (relayoutPending) return;
      relayoutPending = true;
      window.requestAnimationFrame(function () {
        relayoutPending = false;
        relayout();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
